import { Product } from '@/types';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { staggerTransition } from '@/lib/framer-motion/stagger-transition';
import { cn } from '@/lib/utils';
import AnchorLink from '../ui/links/anchor-link';
import NSFWToggle from '../nsfw/nsfw-toggle';
import { useNSFWBlur } from '@/lib/hooks/use-nsfw-blur';

interface InstantSearchResultsProps {
  products: Product[];
  isLoading: boolean;
  onResultClick: () => void;
  onShowAllResults: () => void;
}

export default function InstantSearchResults({
  products,
  isLoading,
  onResultClick,
  onShowAllResults,
}: InstantSearchResultsProps) {
  if (!products?.length && !isLoading) return null;

  const handleShowAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Show all handler triggered'); // Debug log
    onShowAllResults();
    onResultClick();
  };

  return (
    <div className="z-50 w-full rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-dark-200">
      {isLoading ? (
        <div className="p-4 text-center text-sm">Loading...</div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          {products.slice(0, 5).map((product) => {
            const { shouldBlur } = useNSFWBlur(product);

            return (
              <div className="relative" key={product.id}>
                <AnchorLink
                  href={`/products/${product.slug}`}
                  onClick={onResultClick}
                  className={cn(
                    'group block w-full',
                    shouldBlur && 'pointer-events-none'
                  )}
                >
                  <div className="relative flex cursor-pointer items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="relative h-10 w-10 overflow-hidden rounded-md">
                      <Image
                        src={product.image?.thumbnail ?? '/placeholder.svg'}
                        alt={product.name}
                        fill
                        className="object-cover"
                      />
                    </div>

                    <div className="flex flex-1 flex-col">
                      <h3 className="text-xs font-semibold">{product.name}</h3>
                      <span className="text-[11px] text-gray-600 dark:text-gray-400">
                        {product.shop?.name}
                      </span>
                    </div>
                  </div>
                </AnchorLink>

                {shouldBlur && (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 rounded-md bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 backdrop-blur-sm">
                    <div className="bg-dot-pattern opacity-3 absolute inset-0" />
                    <div className="relative flex flex-col items-center">
                      <div className="h-0.5 w-6 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
                      <p className="text-[10px] font-medium text-foreground/80">
                        Adult Content
                      </p>
                      <NSFWToggle size="sm" className="scale-75" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <button
            className="w-full border-t border-gray-100 p-2 text-center text-xs font-medium text-brand hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
            onClick={handleShowAll}
          >
            Show all results
          </button>
        </div>
      )}
    </div>
  );
}
